import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCookieBite } from "@fortawesome/free-solid-svg-icons";
import { Analytics } from "@vercel/analytics/react";
import { motion, AnimatePresence } from "framer-motion";
import cn from "classnames";
import { buttonMutedOnLight, buttonPrimary, linkInlineAccent } from "@/components/ui";

const CookieConsentBanner = () => {
  const { t } = useTranslation();
  const [showBanner, setShowBanner] = useState<boolean | null>(null);
  const [analyticsConsent, setAnalyticsConsent] = useState<boolean | null>(null);

  useEffect(() => {
    const consent = localStorage.getItem("analytics_consent");
    if (consent) {
      setAnalyticsConsent(consent === "true");
      setShowBanner(false);
    } else {
      setShowBanner(true);
    }
  }, []);

  const handleAccept = () => {
    localStorage.setItem("analytics_consent", "true");
    setAnalyticsConsent(true);
    setShowBanner(false);
  };

  const handleDecline = () => {
    localStorage.setItem("analytics_consent", "false");
    setAnalyticsConsent(false);
    setShowBanner(false);
  };

  if (showBanner === null) return null;

  return (
    <>
      {analyticsConsent && <Analytics />}
      <AnimatePresence>
        {showBanner && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 20 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-x-4 bottom-4 lg:right-auto lg:w-fit lg:max-w-sm z-50"
          >
            <div className="rounded-lg border bg-white shadow-lg">
              <div className="flex flex-col space-y-1.5 p-6">
                <div className="flex items-center">
                  <FontAwesomeIcon icon={faCookieBite} className="text-2xl mr-2 text-gray-600" />
                  <h3 className="text-2xl font-semibold leading-none tracking-tight text-gray-900">
                    {t("cookieConsent.title")}
                  </h3>
                </div>
                <p className="text-sm text-gray-600">
                  {t("cookieConsent.messageLead")}{" "}
                  <a href="/privacy" className={cn("underline", linkInlineAccent)}>
                    {t("cookieConsent.privacyPolicyLink")}
                  </a>{" "}
                  {t("cookieConsent.messageTrail")}
                </p>
              </div>
              <div className="flex items-center p-6 pt-0">
                <div className="w-full flex flex-col gap-2">
                  <button
                    className={cn(buttonMutedOnLight, "w-full")}
                    type="button"
                    onClick={handleDecline}
                  >
                    {t("cookieConsent.declineButton")}
                  </button>
                  <button
                    className={cn(buttonPrimary, "w-full")}
                    type="button"
                    onClick={handleAccept}
                  >
                    {t("cookieConsent.acceptButton")}
                  </button>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default CookieConsentBanner;
